import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import type { PartnerType } from "@bookdental/schemas";
import { AJUDA_EQUIPE_E_PARCEIROS } from "@/lib/ajuda/dialogos/equipe-e-parceiros";
import { PARTNER_TYPE_META } from "./partner-meta";
import { CamposDoCadastro } from "./perfil/CamposDoCadastro";
import { useCadastro } from "./perfil/use-cadastro";

/** Novo cadastro: o básico, rápido. Editar e o resto (pedidos, extrato, preços, arquivos) é na
 *  página do cadastro, pra onde o laboratório vai assim que cria. */
export function PartnerDrawer({ tipo, onClose, onCriado }: { tipo: PartnerType | null; onClose: () => void; onCriado: (id: string) => void }) {
  if (!tipo) return null;
  return <NovoCadastro key={tipo} tipo={tipo} onClose={onClose} onCriado={onCriado} />;
}

function NovoCadastro({ tipo, onClose, onCriado }: { tipo: PartnerType; onClose: () => void; onCriado: (id: string) => void }) {
  const { values, setValues, podeEditar, travado, salvar, salvando } = useCadastro({ tipoInicial: tipo, onSalvo: onCriado });
  const meta = PARTNER_TYPE_META[values.type];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        closeLabel="Fechar novo cadastro"
        className="flex max-h-[85dvh] max-w-lg flex-col gap-0 overflow-hidden border-zinc-200 bg-white p-0 shadow-xl dark:border-border dark:bg-card"
      >
        <DialogHeader className="flex-row items-center gap-2.5 space-y-0 border-b border-zinc-100 px-5 py-4 pr-12 text-left dark:border-border">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>
            <meta.icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <DialogDescription className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">Novo cadastro</DialogDescription>
            <DialogTitle className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground" ajuda={AJUDA_EQUIPE_E_PARCEIROS.cadastroDeParceiro}>
              {values.name || meta.label}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <CamposDoCadastro values={values} setValues={setValues} travado={travado} podeEditar={podeEditar} autoFocus />
        </div>

        {/* O rodapé encosta na base da tela; sem a safe area os botões ficam sob a barra de gestos
            do iPhone. Mesmo cálculo já usado na agenda mobile. */}
        <footer className="flex items-center justify-end gap-2 border-t border-zinc-100 px-5 py-3 dark:border-border" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[12px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Cancelar
          </button>
          {podeEditar && (
            <button
              type="button"
              onClick={salvar}
              disabled={salvando}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60"
            >
              {salvando && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Criar cadastro
            </button>
          )}
        </footer>
      </DialogContent>
    </Dialog>
  );
}
